import React, { useState } from 'react';
import { BACKEND_URL } from '../config';

import {
  Check,
  Key,
  PencilSimple,
  Robot,
  Sparkle,
  X
} from '@phosphor-icons/react';
const AgentPreviewModal = ({ 
  isOpen, 
  onClose, 
  roleData,
  opportunityData,
  organizationId,
  onCreateAgent 
}) => {
  const config = opportunityData?.recommended_agent_config || {};
  
  const [agentName, setAgentName] = useState(
    config.agent_name || roleData?.title || ''
  );
  const [agentDescription, setAgentDescription] = useState(
    config.detailed_instructions || opportunityData?.description || roleData?.description || ''
  );
  const [isEditing, setIsEditing] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [showApiKeyInput, setShowApiKeyInput] = useState(false);
  const [hasApiKey, setHasApiKey] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  // Check if user has API key saved
  React.useEffect(() => {
    checkForApiKey();
  }, [organizationId]);

  const checkForApiKey = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/user/api-keys?organization_id=${organizationId}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setHasApiKey(data.has_openai_key || false);
      }
    } catch (error) {
      console.error('Error checking API keys:', error);
    }
  };

  const handleSaveApiKey = async () => {
    if (!apiKey.trim()) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/user/api-keys`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          organization_id: organizationId,
          openai_api_key: apiKey
        })
      });

      if (response.ok) {
        setHasApiKey(true);
        setShowApiKeyInput(false);
        setApiKey('');
        alert('API key saved successfully!');
      } else {
        alert('Failed to save API key');
      }
    } catch (error) {
      console.error('Error saving API key:', error);
      alert('Error saving API key');
    }
  };

  const handleCreateAgent = async () => {
    if (!hasApiKey && !apiKey.trim()) {
      alert('Please add your OpenAI API key first');
      setShowApiKeyInput(true);
      return;
    }

    setIsCreating(true);
    try {
      const agentData = {
        name: agentName,
        description: agentDescription,
        role_id: roleData?.id,
        role_title: roleData?.title,
        department: roleData?.department,
        organization_id: organizationId
      };

      await onCreateAgent(agentData);
      onClose();
    } catch (error) {
      console.error('Error creating agent:', error);
      alert('Failed to create agent');
    } finally {
      setIsCreating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-75">
      <div className="bg-[#1A1B1E] rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-[rgba(255,255,255,0.1)]">
        {/* Header */}
        <div className="sticky top-0 bg-[#1A1B1E] border-b border-[rgba(255,255,255,0.1)] p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-gradient-to-r from-[#6366F1] to-[#EC4899] flex items-center justify-center">
              <Robot size={24} className="text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">AI Agent Preview</h2>
              <p className="text-[#A0AEC0] text-sm">Review and customize before creating</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg glass hover:bg-[rgba(255,255,255,0.1)] transition-colors"
          >
            <X size={24} className="text-[#A0AEC0]" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Role Information */}
          <div className="glass rounded-xl p-4">
            <h3 className="text-lg font-semibold text-white mb-2">For Role</h3>
            <p className="text-xl font-bold text-[#6366F1]">{roleData?.title}</p>
            {roleData?.name && (
              <p className="text-[#A0AEC0] mt-1">{roleData.name}</p>
            )}
            <p className="text-[#718096] text-sm mt-2">{roleData?.department}</p>
          </div>

          {/* Agent Name */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-white font-semibold">Agent Name</label>
              {!isEditing && (
                <button
                  onClick={() => setIsEditing(true)}
                  className="text-[#6366F1] hover:text-[#8B5CF6] text-sm flex items-center gap-1"
                >
                  <PencilSimple size={14} />
                  PencilSimple
                </button>
              )}
            </div>
            {isEditing ? (
              <input
                type="text"
                value={agentName}
                onChange={(e) => setAgentName(e.target.value)}
                className="w-full px-4 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white focus:border-[#6366F1] focus:outline-none"
                placeholder="Enter agent name..."
              />
            ) : (
              <p className="text-lg text-white">{agentName}</p>
            )}
          </div>

          {/* AI Opportunity Description */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-white font-semibold flex items-center gap-2">
                <Sparkle size={18} className="text-[#F59E0B]" />
                AI Agent Opportunity
              </label>
            </div>
            {isEditing ? (
              <textarea
                value={agentDescription}
                onChange={(e) => setAgentDescription(e.target.value)}
                rows={6}
                className="w-full px-4 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white focus:border-[#6366F1] focus:outline-none resize-none"
                placeholder="Describe what this AI agent will do..."
              />
            ) : (
              <div className="glass rounded-lg p-4">
                <p className="text-[#E2E8F0] leading-relaxed whitespace-pre-wrap">
                  {agentDescription || 'No description available'}
                </p>
              </div>
            )}
            {isEditing && (
              <button
                onClick={() => setIsEditing(false)}
                className="mt-2 px-4 py-2 bg-[#6366F1] text-white rounded-lg hover:bg-[#8B5CF6] transition-colors flex items-center gap-2"
              >
                <Check size={16} />
                Save Changes
              </button>
            )}
          </div>

          {/* API Key Section */}
          <div className="glass rounded-xl p-4 border-2 border-[#6366F1]">
            <div className="flex items-center gap-2 mb-3">
              <Key size={20} className="text-[#6366F1]" />
              <h3 className="text-lg font-semibold text-white">OpenAI API Key Required</h3>
            </div>
            
            {hasApiKey ? (
              <div className="flex items-center gap-2 text-[#10B981]">
                <Check size={18} />
                <p>API key configured</p>
              </div>
            ) : showApiKeyInput ? (
              <div className="space-y-3">
                <p className="text-[#A0AEC0] text-sm">
                  You need to provide your own OpenAI API key to create AI agents. 
                  Your key is encrypted and stored securely.
                </p>
                <input
                  type="password"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  className="w-full px-4 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white focus:border-[#6366F1] focus:outline-none"
                  placeholder="sk-..."
                />
                <div className="flex gap-2">
                  <button
                    onClick={handleSaveApiKey}
                    className="px-4 py-2 bg-[#6366F1] text-white rounded-lg hover:bg-[#8B5CF6] transition-colors"
                  >
                    Save API Key
                  </button>
                  <button
                    onClick={() => setShowApiKeyInput(false)}
                    className="px-4 py-2 glass text-white rounded-lg hover:bg-[rgba(255,255,255,0.1)] transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-[#A0AEC0] text-sm">
                  Add your OpenAI API key to create AI agents
                </p>
                <button
                  onClick={() => setShowApiKeyInput(true)}
                  className="px-4 py-2 bg-[#6366F1] text-white rounded-lg hover:bg-[#8B5CF6] transition-colors flex items-center gap-2"
                >
                  <Key size={16} />
                  Add API Key
                </button>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              onClick={handleCreateAgent}
              disabled={isCreating || (!hasApiKey && !apiKey.trim())}
              className="flex-1 relative overflow-hidden rounded-lg group disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-[#6366F1] to-[#EC4899]"></div>
              <span className="relative z-10 flex items-center justify-center gap-2 py-3 text-white font-bold">
                <Robot size={20} />
                {isCreating ? 'Creating Agent...' : 'Create AI Agent'}
              </span>
            </button>
            <button
              onClick={onClose}
              className="px-6 py-3 glass text-white rounded-lg hover:bg-[rgba(255,255,255,0.1)] transition-colors font-semibold"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentPreviewModal;
